import type {
  SolarComputeOptionDto,
  SolarSelectedComputeDto,
} from "~/api/dto/solar-proposal-compute.dto"
import useSolarComputePreview from "~/api/hooks/useSolarComputePreview"
import useSolarProposalCompute from "~/api/hooks/useSolarProposalCompute"
import useSolarProposalProductOffering from "~/api/hooks/useSolarProposalProductOffering"
import useUpdateSolarProposalCompute from "~/api/hooks/useUpdateSolarProposalCompute"
import {
  SOLAR_COMPUTE_INVALID_CODE,
  solarProposalErrorCode,
  solarProposalErrorMessages,
} from "~/api/solar-proposal-errors"
import { parseAxiosError } from "~/utils/parseAxiosError"

import { SOLAR_SETUP_DRAWER_TITLES } from "./solar-setup-paths"
import { SolarLockedNote } from "./SolarLockedNote"
import {
  COMPUTE_CARD_GRID_CLASS,
  COMPUTE_SAVED_ON_PROPOSAL,
  ComputeCard,
  ComputeCatalog,
} from "../ProposalSetup/compute/ComputeCatalog"
import {
  PricingLoadError,
  PricingLoading,
  PricingStepCloseFooter,
  PricingStepDrawer,
  ToggleCardGrid,
} from "../ProposalSetup/pricing/pricing-step-chrome"

export const COMPUTE_EMPTY_NOTICE = `AI compute isn't offered for this proposal's location, utility or Finance Program.`
export const COMPUTE_NO_DESIGN_NOTICE = `This proposal has no active design yet`
export const COMPUTE_NOT_EDITABLE_NOTICE = `Editing this finance partner isn't available in V2 yet`
const TEST_ID_PREFIX = `solar-compute`

const isInvalidError = (error: unknown): boolean =>
  solarProposalErrorCode(error) === SOLAR_COMPUTE_INVALID_CODE

/** The Solar command and codes behind the shared catalog. */
const SolarComputeCatalog = (props: {
  proposalId: string
  options: readonly SolarComputeOptionDto[]
  saved: SolarSelectedComputeDto | null
  savedHint?: string
  onClose: () => void
}): JSX.Element => (
  <ComputeCatalog
    {...props}
    update={useUpdateSolarProposalCompute()}
    isInvalidError={isInvalidError}
    errorMessages={solarProposalErrorMessages}
    emptyNotice={COMPUTE_EMPTY_NOTICE}
    testIdPrefix={TEST_ID_PREFIX}
  />
)

/**
 * Compute: the AI compute node on the Active Design, zero or one, from what
 * PowurOS offers the proposal. The saved node and lock come from the pure
 * read; the options from the preview. A save makes pricing and savings out of
 * date; nothing here recalculates or adds up money.
 */
export const SolarComputeStep = ({
  proposalId,
  onClose,
}: {
  proposalId: string
  /** Closes the drawer, also after a save lands. */
  onClose: () => void
}): JSX.Element => {
  const compute = useSolarProposalCompute(proposalId)
  const offering = useSolarProposalProductOffering(proposalId)
  const read = compute.data
  // Until the partner is known the step stays read-only: no preview is asked
  // of PowurOS for a partner V2 can't edit.
  const partnerEditable = offering.data?.partner.editable === true
  const editable =
    read != null &&
    !read.financingLocked &&
    !read.noActiveDesign &&
    partnerEditable
  const preview = useSolarComputePreview(proposalId, { enabled: editable })
  const title = SOLAR_SETUP_DRAWER_TITLES.compute

  const loadFailed = compute.isError
    ? compute
    : offering.isError
      ? offering
      : null
  if (!read || (offering.data == null && !read.noActiveDesign)) {
    return (
      <PricingStepDrawer title={title} onClose={onClose}>
        {loadFailed ? (
          <PricingLoadError
            message={parseAxiosError(
              loadFailed.error,
              `The AI compute node could not be loaded.`,
            )}
            onRetry={() => void loadFailed.refetch()}
          />
        ) : (
          <PricingLoading>Loading AI compute</PricingLoading>
        )}
        <PricingStepCloseFooter onClose={onClose} />
      </PricingStepDrawer>
    )
  }

  if (!editable) {
    const notice = read.noActiveDesign
      ? COMPUTE_NO_DESIGN_NOTICE
      : read.financingLocked
        ? (read.financingLockReason ?? `Financing locks this selection.`)
        : (offering.data?.partner.reason ?? COMPUTE_NOT_EDITABLE_NOTICE)
    return (
      <PricingStepDrawer title={title} onClose={onClose}>
        <div className="mb-4">
          <SolarLockedNote testId="solar-compute-locked">
            {notice}
          </SolarLockedNote>
        </div>
        {read.selected ? (
          <ToggleCardGrid
            testId="solar-compute-cards"
            className={COMPUTE_CARD_GRID_CLASS}
          >
            <ComputeCard
              title={read.selected.name}
              data={read.selected.computerData}
              payout={read.selected.payout}
              selected
              disabled
              testIdPrefix={TEST_ID_PREFIX}
              onToggle={() => undefined}
            />
          </ToggleCardGrid>
        ) : null}
        <PricingStepCloseFooter onClose={onClose} />
      </PricingStepDrawer>
    )
  }

  return (
    <PricingStepDrawer title={title} onClose={onClose}>
      {preview.isLoading ? (
        <>
          <PricingLoading>Loading AI compute nodes from PowurOS</PricingLoading>
          <PricingStepCloseFooter onClose={onClose} />
        </>
      ) : preview.isError ? (
        <>
          <PricingLoadError
            message={parseAxiosError(
              preview.error,
              `Couldn't load AI compute nodes from PowurOS. Try again.`,
            )}
            onRetry={() => void preview.refetch()}
          />
          {read.selected ? (
            // Removing the node needs nothing from PowurOS.
            <SolarComputeCatalog
              proposalId={proposalId}
              options={[]}
              saved={read.selected}
              savedHint={COMPUTE_SAVED_ON_PROPOSAL}
              onClose={onClose}
            />
          ) : (
            <PricingStepCloseFooter onClose={onClose} />
          )}
        </>
      ) : (
        <SolarComputeCatalog
          proposalId={proposalId}
          options={preview.data?.options ?? []}
          saved={read.selected}
          onClose={onClose}
        />
      )}
    </PricingStepDrawer>
  )
}
